import { AuthNet, NetEssentials } from 'networking';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { AccountFlag, Type2FA } from 'prismas';
import { useState } from 'react';
import { useAlerts, useFlags, useUser } from 'state';
import { B4KUser, Form, FormErrors, authSchemas, handleError, type ZInfer } from 'types';
import { z } from 'zod';
import { Input } from '../components/form/input';
import { LogoMainLong } from '../components/img/logo';
import { Meta } from '../components/meta/meta';

type TwoFAData = { fname: string; lname: string; defaultType: Type2FA; options: Type2FA[] };
type VerificationData = { fname: string; lname: string };

export default function Login() {
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState<Form<typeof authSchemas.general.data.loginParams>>({
    email: '',
    password: '',
    rememberMe: true,
  });
  const [errors, setErrors] = useState<
    FormErrors<typeof authSchemas.general.login> &
      FormErrors<typeof authSchemas.general.twoFA> &
      FormErrors<typeof authSchemas.general.verification>
  >();

  const [verificationData, setVerificationData] = useState<VerificationData>(null);
  const [verificationForm, setVerificationForm] = useState<Form<typeof authSchemas.general.verification>>({
    code: '',
  });

  const [twoFaData, setTwoFaData] = useState<TwoFAData>(null);
  const [twoFaForm, setTwoFaForm] = useState<Form<typeof authSchemas.general.twoFA>>({
    type: 'AUTHENTICATOR',
    code: '',
  });
  const [usingBackupCode, setUsingBackupCode] = useState(false);

  const router = useRouter();
  const { addAlert } = useAlerts();
  const {
    flags: { openRegistration },
  } = useFlags();

  return (
    <main className="container mx-auto flex min-h-full max-w-3xl flex-col justify-center px-4 pb-36 pt-12 sm:px-6 lg:px-8">
      <Meta title="Login" noAuthorized />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <LogoMainLong className="mx-auto mb-4 h-24 w-auto" />
        <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900">Sign in to your account</h2>
        {openRegistration ? (
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link href="/register" className="font-medium text-green-600 hover:text-green-500">
              create one!
            </Link>
          </p>
        ) : (
          <p className="mt-2 text-center text-sm text-gray-600">(Referral Agencies and Recurring Donors!)</p>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white px-4 py-8 shadow sm:rounded-lg sm:px-10">{renderForm()}</div>
      </div>
    </main>
  );

  function renderForm() {
    if (verificationData) return renderVerificationForm();
    if (twoFaData) return renderTwoFAForm();
    return loginForm();
  }

  function renderVerificationForm() {
    return (
      <form className="space-y-6" onSubmit={login}>
        <div className="flex flex-col items-center gap-1 text-center">
          <h2 className="text-xl">
            Welcome, {verificationData.fname} {verificationData.lname}
          </h2>
          <p className="text-sm">
            Please enter the 8 digit verification code sent to your email. If you did not receive a code, please check
            your spam folder or{' '}
            <button type="button" onClick={resendVerif} className="font-medium text-blue-600 hover:text-blue-500">
              resend
            </button>
            .
          </p>
        </div>

        <Input
          iType="classic"
          name="Verification Code"
          fKey="code"
          form={verificationForm}
          setForm={setVerificationForm}
          errors={errors}
          setErrors={setErrors}
        />

        <div>
          <button
            type="submit"
            className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
            Submit Code
          </button>
        </div>
      </form>
    );
  }

  function renderTwoFAForm() {
    return (
      <form className="space-y-6" onSubmit={login}>
        <div className="flex flex-col items-center gap-1 text-center">
          <h2 className="text-xl">
            Welcome, {twoFaData.fname} {twoFaData.lname}
          </h2>
          <p className="text-sm">
            {!usingBackupCode ? (
              <>Please enter the 2FA code from your {twoFaData.defaultType.toLowerCase()} app.</>
            ) : (
              'Please enter a backup code to login.'
            )}
          </p>
        </div>

        <Input
          iType="classic"
          name={usingBackupCode ? 'Backup Code' : '2FA Code'}
          fKey="code"
          form={twoFaForm}
          setForm={setTwoFaForm}
          errors={errors}
          setErrors={setErrors}
        />

        <div>
          <button
            type="submit"
            className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
            Submit Code
          </button>
          <button
            className="mt-1 w-full text-center text-xs text-blue-500 hover:underline"
            type="button"
            onClick={() => {
              setTwoFaForm({ type: usingBackupCode ? 'AUTHENTICATOR' : 'BACKUP_CODES', code: '' });
              setErrors(null);
              setUsingBackupCode(u => !u);
            }}>
            {usingBackupCode ? 'Use 2FA Code' : 'Use Backup Code'}
          </button>
        </div>
      </form>
    );
  }

  function loginForm() {
    return (
      <form className="space-y-6" onSubmit={login}>
        <Input
          iType="classic"
          type="email"
          name="Email"
          autoComplete="email"
          fKey="email"
          form={form}
          setForm={setForm}
          errors={errors}
          setErrors={setErrors}
        />
        <Input
          iType="classic"
          name="Password"
          type="password"
          autoComplete="current-password"
          fKey="password"
          form={form}
          setForm={setForm}
          errors={errors}
          setErrors={setErrors}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={form.rememberMe}
              onChange={e => setForm(f => ({ ...f, rememberMe: e.target.checked }))}
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
              Remember me
            </label>
          </div>

          <div className="text-sm">
            <button type="button" onClick={forgot} className="font-medium text-blue-600 hover:text-blue-500">
              Forgot your password?
            </button>
          </div>
        </div>

        <div>
          <button
            type="submit"
            className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
            Sign in
          </button>
        </div>
      </form>
    );
  }

  async function login(e?: React.FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (loading) return;

    const validate = await authSchemas.general.data.loginParams.spa(form);
    if (validate.success === false) return setErrors(validate.error.format());
    setErrors(null);

    let verification: ZInfer<typeof authSchemas.general.verification>;
    if (verificationData) {
      const verificationValidate = authSchemas.general.verification.safeParse(verificationForm);
      if (verificationValidate.success === false) return setErrors(verificationValidate.error.format());
      verification = verificationValidate.data;
    }

    let twoFA: ZInfer<typeof authSchemas.general.twoFA>;
    if (twoFaData) {
      const twoFaValidate = authSchemas.general.twoFA.safeParse(twoFaForm);
      if (twoFaValidate.success === false) return setErrors(twoFaValidate.error.format());
      twoFA = twoFaValidate.data;
    }

    setLoading(true);
    // const token = await execute('login');
    const res = await AuthNet.auth.login({ login: validate.data, twoFA, verification });
    setLoading(false);

    if (res.success === true) return await handleLogin(res.data.user);

    if (res.error.message === 'not verified') {
      if (!twoFaData) return setVerificationData(res.error);

      if (res.error.expired)
        addAlert({
          title: 'Expired Email Verification Code',
          body: 'We have sent you a new code. Please check your email.',
          icon: 'error',
        });
      else
        addAlert({
          title: 'Invalid Email Verification Code',
          body: 'Please check your code and try again.',
          icon: 'error',
        });

      return setVerificationForm({ code: '' });
    }

    if (res.error.message === '2fa') {
      if (!twoFaData) return setTwoFaData(res.error);

      addAlert({
        title: 'Invalid 2FA Code',
        body: 'Please check your code and try again.',
        icon: 'error',
      });

      return setTwoFaForm({ ...twoFaForm, code: '' });
    }

    switch (res.error.message) {
      case 'invalid credentials':
        handleError(setErrors, 'password', 'Invalid email or password');
        break;
      case 'Rate limit exceeded':
        addAlert({
          title: 'Too Many Attempts',
          body: 'Please wait a minute before trying again.',
          icon: 'error',
        });
        break;
      case AccountFlag.SUSPENDED:
        addAlert({
          title: 'Account Suspended',
          body: 'Your account has been suspended. Please contact support.',
          icon: 'error',
        });
        break;
      case AccountFlag.BANNED:
        addAlert({
          title: 'Account Banned',
          body: 'Your account has been banned. Please contact support.',
          icon: 'error',
        });
        break;
    }
  }

  async function forgot() {
    if (loading) return;
    if (!form.email) return handleError(setErrors, 'email', 'Email is required');

    if (z.string().email().safeParse(form.email).success === false)
      return handleError(setErrors, 'email', 'Invalid email');

    setLoading(true);

    await AuthNet.password.forgot(form.email);

    addAlert({ title: 'Password Reset Requested', body: 'Check your email for a reset link.' });
    setLoading(false);
  }

  async function resendVerif() {
    if (loading) return;
    setLoading(true);

    await AuthNet.email.resendConfirmation(form.email);

    addAlert({ title: 'Verification Code Resent', body: 'Check your email for a new code.' });
    setLoading(false);
  }

  async function handleLogin(user: B4KUser, noRedirect?: boolean) {
    NetEssentials.loggedIn = true;
    useUser.getState().setUser(user);
    await import('../../../apps/web/scripts/appLaunch').then(({ appLaunch }) => appLaunch(true));

    if (noRedirect) return;
    if (router.query.r && typeof router.query.r === 'string') return await router.replace(router.query.r);
    await router.replace('/dashboard');
  }
}
